/**
 * Error Tracking Module
 *
 * Provides global error handlers to capture unhandled errors and send them
 * to our custom backend analytics. Privacy-first approach.
 */

import { backendAdapter } from './backendAdapter';

/**
 * Initialize global error handlers
 * Call this once at app startup
 */
export function initErrorTracking(): void {
    if (typeof window === 'undefined') return;

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const error = event.reason;
        const errorMessage = error instanceof Error
            ? error.message
            : String(error);

        backendAdapter.trackError(errorMessage, {
            type: 'unhandled_rejection',
            stack: error instanceof Error ? error.stack?.substring(0, 1000) : undefined,
        });
    });

    // Catch unhandled errors
    window.addEventListener('error', (event: ErrorEvent) => {
        // Ignore errors from browser extensions or cross-origin scripts
        if (!event.filename || event.filename.includes('extension://')) {
            return;
        }

        backendAdapter.trackError(event.message, {
            type: 'unhandled_error',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            stack: event.error?.stack?.substring(0, 1000),
        });
    });

    console.log('[Error Tracking] Initialized');
}

/**
 * Manually track an error (for caught exceptions)
 */
export function trackError(
    error: Error | string,
    context?: Record<string, unknown>
): void {
    const errorMessage = error instanceof Error ? error.message : error;
    const stack = error instanceof Error ? error.stack?.substring(0, 1000) : undefined;

    backendAdapter.trackError(errorMessage, {
        type: 'handled_error',
        stack,
        ...context,
    });
}

/**
 * Track a React Error Boundary error
 */
export function trackBoundaryError(
    error: Error,
    componentStack?: string
): void {
    backendAdapter.trackError(error.message, {
        type: 'react_boundary',
        stack: error.stack?.substring(0, 1000),
        componentStack: componentStack?.substring(0, 500),
    });
}
